/**
 * RICERCA REMOTA — la parte senza React: quando cercare, come fondere, quando dirlo.
 *
 * ## Il difetto che questo file esiste per chiudere
 *
 * Una tendina che fa `fetchList('projects', { page: { size: 100 } })` e poi filtra in memoria
 * **mente**: mostra le prime 100 righe e si comporta come se fossero tutte. Chi digita il codice
 * della commessa 137ª non trova niente e conclude che la commessa non c'è — non che la tendina
 * non è arrivata a leggerla. È il difetto peggiore della categoria, perché non somiglia a un
 * errore: somiglia a un dato mancante.
 *
 * Lo stesso vale, in modo più insidioso, per `fetchAllPages(..., hardCap)`: pagina davvero, ma
 * quando il dataset supera il tetto si ferma **senza dirlo**. Un tetto che nessuno vede è un
 * tetto che nessuno alza.
 *
 * ## Le tre decisioni
 *
 * 1. **Si cerca sul server, da 3 caratteri.** Sotto i 3 la tendina non prova nemmeno: cercare
 *    «a» significa chiedere al server di rimandare mezza anagrafica per poi troncarla, cioè
 *    ricreare il problema un livello più in là. Sopra i 3 la ricerca è selettiva e le 100 righe
 *    di una pagina bastano quasi sempre — e quando non bastano lo si scrive.
 *
 * 2. **Due query, non un `or`.** Cercare in «codice **oppure** nome» vorrebbe dire
 *    `or(contains(code,…),contains(name,…))`, e il builder JSON:API di questo progetto compone
 *    solo `and` (`buildQueryString`: più campi → `and(...)`). Un'espressione grezza a mano
 *    aggirerebbe il builder con una sintassi che da qui **non posso verificare** contro il BE, e
 *    un `filter` che il server non capisce non torna 400: torna `200` **non filtrato** — cioè
 *    esattamente l'elenco troncato di prima, con l'aria di funzionare. Quindi: una query per
 *    campo, con gli operatori già in uso in produzione (`like` → `contains`), e i risultati
 *    fusi qui. Due richieste ogni 300 ms di digitazione sono un prezzo che si vede nei log, non
 *    nell'uso.
 *
 * 2bis. **Più parole, una query per parola.** «andrea marcelli» non è contenuto né nel nome né
 *    nel cognome: cercato intero non trova nessuno. Si interroga ogni parola su ogni campo e
 *    si tengono le righe che le contengono tutte, in qualunque campo.
 *
 * 3. **Il tetto si dichiara.** Quando una pagina torna piena e il totale è maggiore, la tendina
 *    dice «prime 100 di 340: affina». Il numero esatto viene dal server (`meta.total`), non da
 *    una stima.
 */
import { formatNumber } from '@/shared/lib/format';

/** Sotto questa soglia non si interroga il server. */
export const MIN_CHARACTERS = 3;

/** Righe per pagina: il massimo che il BE accetta (oltre → 400, e il builder lo clampa). */
export const PAGE = 100;

/**
 * Una riga cercabile: ha un id e i campi su cui si cerca. Volutamente minimale — la tendina
 * non ha bisogno di sapere che cos'è una commessa.
 */
export interface SearchableRow {
  id: string;
  [field: string]: unknown;
}

/** Lo stato in cui si trova la ricerca. Cinque valori, non un booleano. */
export type SearchStatus =
  /** Meno di `MIN_CARATTERI`: non si è ancora cercato niente. */
  | 'da-digitare'
  /** Interrogazione in corso. */
  | 'ricerca'
  /** Il server ha risposto male. */
  | 'errore'
  /** Ha risposto, e non c'è nulla che corrisponda. */
  | 'vuoto'
  /** Ci sono risultati. */
  | 'risultati';

/**
 * Il testo digitato è abbastanza per cercare?
 *
 * Si conta sul testo **ripulito**: tre spazi non sono tre caratteri, e senza `trim` la tendina
 * partirebbe con una query che non filtra niente.
 */
export function mustSearch(text: string, min: number = MIN_CHARACTERS): boolean {
  return text.trim().length >= min;
}

/** Al massimo tante parole interrogate: ognuna costa una query per campo. */
export const MAX_TOKENS = 3;

/** Minuscolo e senza accenti: «Nicolò» si trova scrivendo «nicolo». */
export function normalizeSearch(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Le parole digitate, senza doppioni. «andrea marcelli» → due parole. */
export function searchTokens(text: string): string[] {
  return [...new Set(text.trim().split(/\s+/).filter(Boolean))];
}

/** Le parole da mandare al server: le più lunghe, che filtrano di più. */
export function serverTokens(text: string): string[] {
  return [...searchTokens(text)].sort((x, y) => y.length - x.length).slice(0, MAX_TOKENS);
}

/** Ogni parola compare nel testo, in qualunque ordine. */
export function matchesAllTokens(haystack: string, tokens: readonly string[]): boolean {
  const h = normalizeSearch(haystack);
  return tokens.every((t) => h.includes(normalizeSearch(t)));
}

/** Il valore di un percorso puntato (`customer.client.organization`) su una riga denormalizzata. */
export function valueAtPath(row: unknown, path: string): string {
  let cur: unknown = row;
  for (const k of path.split('.')) {
    if (cur === null || typeof cur !== 'object') return '';
    cur = (cur as Record<string, unknown>)[k];
  }
  return typeof cur === 'string' || typeof cur === 'number' ? String(cur) : '';
}

/**
 * Fonde i risultati delle query per campo, togliendo i doppioni.
 *
 * La stessa commessa esce sia da `contains(code,'ROS')` sia da `contains(name,'ROS')`: senza
 * questo passaggio comparirebbe due volte nella tendina, e una tendina con due righe identiche
 * fa dubitare che ci siano due commesse.
 *
 * L'ordine di uscita è quello di **primo incontro**, non l'ordine di una lista sull'altra: così
 * il `sort` chiesto al server (di solito `code`) resta leggibile invece di essere sostituito da
 * un ordinamento locale che il server non conosce.
 */
export function mergeById<T extends SearchableRow>(...lists: readonly (readonly T[])[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const list of lists) {
    for (const r of list) {
      if (!r || typeof r.id !== 'string' || r.id === '' || seen.has(r.id)) continue;
      seen.add(r.id);
      out.push(r);
    }
  }
  return out;
}

/**
 * Quante righe il server dice di avere in più di quelle mostrate.
 *
 * `0` quando non c'è troncamento. Si prende il **massimo** fra le query per campo invece di
 * sommarle: sommare due totali che si sovrappongono produce un numero più grande del vero, e un
 * avviso che esagera si impara a ignorare.
 */
export function truncation(responses: readonly { rows: number; total: number }[], page: number = PAGE): number {
  let worst = 0;
  for (const r of responses) {
    if (r.rows >= page && r.total > r.rows) worst = Math.max(worst, r.total - r.rows);
  }
  return worst;
}

/** Lo stato della tendina, da mostrare invece di un elenco vuoto senza spiegazione. */
export function searchStatus(a: {
  text: string;
  min?: number;
  loading: boolean;
  error: boolean;
  results: number;
}): SearchStatus {
  if (!mustSearch(a.text, a.min ?? MIN_CHARACTERS)) return 'da-digitare';
  if (a.loading) return 'ricerca';
  if (a.error) return 'errore';
  return a.results > 0 ? 'risultati' : 'vuoto';
}

/**
 * Il messaggio dello stato «non hai ancora digitato», con il totale quando lo conosciamo.
 *
 * Dire «digita almeno 3 caratteri» spiega il gesto; dire «sono 340» spiega **perché** il gesto
 * serve, ed è la differenza fra un vincolo arbitrario e un vincolo capito. Senza totale si dice
 * solo il gesto: inventare un numero per completare la frase sarebbe peggio che tacerlo.
 */
export function messageToType(min: number, total: number | null, pluralName: string): string {
  const gesture = `Digita almeno ${min} caratteri per cercare`;
  if (total === null || total <= 0) return `${gesture} fra ${pluralName}.`;
  return `${gesture}: ${pluralName} sono ${formatNumber(total)} e non entrano in una tendina.`;
}

/**
 * Il messaggio di ogni stato **che non è un elenco**, uno solo per tutti i contenitori.
 *
 * Sta qui e non nel componente perché le tendine remote sono due — `<RemoteSelect>` e la
 * pastiglia dei filtri task — e due copie delle stesse frasi divergono: la prima volta che una
 * impara a dire «non ho potuto cercare» e l'altra no, la seconda mente.
 *
 * `null` sullo stato `risultati`: lì l'elenco parla da sé.
 */
export function statusMessage(
  stato: SearchStatus,
  a: { min: number; total: number | null; pluralName: string; text: string },
): string | null {
  switch (stato) {
    case 'da-digitare':
      return messageToType(a.min, a.total, a.pluralName);
    case 'ricerca':
      return 'Cerco…';
    case 'errore':
      return 'Ricerca non riuscita: il sistema non ha risposto. Riprova, oppure scrivi il codice esatto.';
    case 'vuoto':
      /* Senza testo il «per «»» sarebbe una frase rotta: capita sull'anagrafica sfogliabile che
         si svuota (tutti disattivati, filtro base che non lascia righe). */
      return a.text.trim()
        ? `Nessuna corrispondenza per «${a.text.trim()}».`
        : `Nessun risultato fra ${a.pluralName}.`;
    default:
      return null;
  }
}

/** Il messaggio del troncamento: quante se ne vedono, quante ce n'è, e cosa fare. */
export function truncatedMessage(shown: number, beyond: number, nomePlurale: string): string {
  return `Mostrate ${shown} ${nomePlurale} su ${formatNumber(shown + beyond)}:`
    + ' aggiungi qualche carattere per restringere.';
}
